import classNames from 'clsx';
import { path } from 'ramda';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { DISCOUNT_IN_OUTFLOW_SAVE_SHOW_TIMEOUT } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { abPauseSubscription } from 'reactApp/appHelpers/featuresHelpers/features/pauseSubscription';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { TBankWarning } from 'reactApp/components/TBankMobileBlock/TBankMobileBlock';
import { type Partner, PARTNER_TITLE } from 'reactApp/modules/billingData/billing.data';
import { isSubscriptionRenewable } from 'reactApp/modules/billingData/billingData.utils';
import { BuySubscriptionActions } from 'reactApp/modules/buySubscription/buySubscription.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { isTBankSubscription, isVkMusicReward } from 'reactApp/modules/promoTariffs/promoTariffs.types';
import { getPausedSubscriptionStorageKey, isPausedSubscription } from 'reactApp/modules/subscriptions/subscriptions.helpers';
import { UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import { isVkMusicSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { SubscriptionFooter } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionFooter/SubscriptionFooter';
import { SubscriptionInfoBlock } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/SubscriptionInfoBlock/SubscriptionInfoBlock';
import { useInfo } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/useInfo';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { useContent } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/useContent';
import { useSubscription } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/useSubscription';
import type { RootState } from 'reactApp/store';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { VkMusicRewardInfo } from 'reactApp/ui/VkMusicRewardInfo/VkMusicRewardInfo';

import styles from './SubscriptionModal.css';
import { useOpenDiscountInOutflowSaveModalCallback } from './useOpenDiscountInOutflowSaveModalCallback';

interface IProps {
    onClose: () => void;
    subscriptionId: string;
}

// TODO заменить any на явный тип при замене promo/list на biling/data
const SubscriptionModalCore = ({ subscription, subscriptionId, onClose }: IProps & { subscription: any }) => {
    const dispatch = useDispatch();
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, subscription?.['productId']));
    const pausedSubscription: boolean =
        useSelector((state: RootState) => UserStorageSelectors.get(state, getPausedSubscriptionStorageKey(subscription.id))) &&
        isPausedSubscription(abPauseSubscription, subscription.renewAt);
    const partner = path<Partner>(['partner'], subscription);
    // tempexp_18907-next-line
    const openDiscountInOutflowSaveModal = useOpenDiscountInOutflowSaveModalCallback({ subscription });

    const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);

    const { header, warning } = useContent({ subscription, product, partner });
    const subscriptionsInfos = useInfo({ subscription, product, partner, sendAnalytics, paused: pausedSubscription });

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.CANCEL_SUB_TARIFF);
    }, []);

    const onPartnerClick = useCallback(() => {
        sendAnalytics(ESubscriptionModalAction.partnerClick);
    }, [sendAnalytics]);

    const { autorenewal } = subscription;

    const isRenewable = product && isSubscriptionRenewable(subscription);
    const canCheckDiscount = product && isSubscriptionRenewable(subscription, true);

    const footer = useMemo(() => {
        if (isVkMusicReward(subscription) && subscription?.accountLink) {
            return <VkMusicRewardInfo accountLink={subscription.accountLink} />;
        }

        if (isTBankSubscription(subscription)) {
            return <TBankWarning />;
        }

        if (warning && partner && subscription?.accountLink) {
            return (
                <div className={styles.warning}>
                    {warning}&nbsp;
                    <a target="_blank" className={styles.link} href={subscription.accountLink} rel="noreferrer" onClick={onPartnerClick}>
                        {PARTNER_TITLE[partner]}
                    </a>
                </div>
            );
        }

        if (warning) {
            return <div className={styles.warning}>{warning}</div>;
        }

        return (
            !subscription?.isFreemium &&
            product && <SubscriptionFooter subscription={subscription} product={product} sendAnalytics={sendAnalytics} />
        );
    }, [warning, partner, product, subscription, sendAnalytics, onPartnerClick]);

    useEffect(() => {
        if (subscription) {
            sendAnalytics(ESubscriptionModalAction.open);
            emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_OPEN, { isTrial: subscription.isTrial });
        }
        // Исключаем проверки предложений в оттоке по дополнительным общим признакам
        if (isVkMusicSubscription(subscription)) {
            return;
        }
        if (abPauseSubscription && (isRenewable || autorenewal)) {
            dispatch(BuySubscriptionActions.checkPauseSubscription({ subscriptionId }));
        }
        if (canCheckDiscount) {
            dispatch(
                BuySubscriptionActions.checkDiscountInOutflow({
                    subscriptionId,
                })
            );
        }
    }, [dispatch, sendAnalytics, subscription, subscriptionId, isRenewable, canCheckDiscount]);

    return (
        <Dialog
            open
            preventStackDimmer
            header={<div className={styles.header}>{header}</div>}
            id="subscription-modal"
            onCancel={() => {
                sendAnalytics(ESubscriptionModalAction.close);
                onClose?.();
                // tempexp_18907-next-line
                setTimeout(openDiscountInOutflowSaveModal, DISCOUNT_IN_OUTFLOW_SAVE_SHOW_TIMEOUT);
            }}
            className={styles.root}
            footer={footer}
            size="tiny"
        >
            <Content isModal wrapClass={classNames(styles.content, { [styles.withoutFooter]: !footer })}>
                {subscriptionsInfos.map((info) => (
                    <SubscriptionInfoBlock
                        title={info.title}
                        key={info.id}
                        id={info.id}
                        footer={info.footer}
                        qaId={`subscription-info-${info.id}`}
                        isFreemium={subscription?.isFreemium}
                    >
                        {info.content}
                    </SubscriptionInfoBlock>
                ))}
            </Content>
        </Dialog>
    );
};

export const SubscriptionModal = memo(({ subscriptionId, onClose }: IProps): ReactElement | null => {
    const subscription = useSubscription({ id: subscriptionId });
    if (!subscription) {
        return (
            <BaseConfirmDialog
                className={styles.noSubsModal}
                buttons={[EButtonType.action]}
                actionButtonText="Закрыть"
                size="middle"
                closeOnDimmerClick
                closable
                renderHeader={() => 'Такой подписки нет'}
                renderContent={() => (
                    <span className={styles.text}>
                        Проверьте, корректно&nbsp;ли указана ссылка.
                        <br />
                        Или обратитесь в&nbsp;поддержку
                    </span>
                )}
                onClose={onClose}
                onAction={onClose}
            />
        );
    }
    return <SubscriptionModalCore subscription={subscription} subscriptionId={subscriptionId} onClose={onClose} />;
});

SubscriptionModal.displayName = 'SubscriptionModal';
